import { YuLifeLocale } from "hooks";
import { StaticRoutes } from "@routes";
import { Anchor } from "components/atoms/Anchor";
import { color } from "@styles";
import { ReactElement } from "react";

type Data = Record<
  YuLifeLocale,
  {
    products: Product[];
    zigzag?: Zigzag;
    productSubheader?: ProductSubheader;
    caseStudyData?: AdviserResourceCard[];
  }
>;

interface Product {
  key: string;
  name: string;
  iconName?: string;
  description: string;
  href?: string;
  isLive?: boolean;
  justifyContent?: string;
}

interface Zigzag {
  description: ReactElement;
  description2: ReactElement;
}

interface ProductSubheader {
  subheader: ReactElement;
}

interface AdviserResourceCard {
  imgSrc: string;
  imgAlt: string;
  title: string;
  resourceType?: "DOC" | "Presentation" | "PDF";
  description: string;
  slug: string;
  linkText?: string;
}

const data: Data = {
  uk: {
    products: [
      {
        iconName: "Health",
        key: "Group Health Insurance",
        name: "Group Health Insurance",
        description:
          "Covers the cost of high-quality healthcare, quickly, if employees were to get ill. Provided by Bupa.",
        href: StaticRoutes.insuranceGroupHealthInsurance,
      },
      {
        key: "Group Life Insurance",
        name: "Group Life Insurance",
        iconName: "GroupLife",
        description: "Financial support for employees’ beneficiaries in the event of their death.",
        href: StaticRoutes.insuranceGroupLifeInsurance,
      },
      {
        key: "Group Income Protection",
        name: "Group Income Protection",
        iconName: "GroupIncomeProtection",
        description:
          "Covers a proportion of an employee’s income in the event that they are unable to work due to ill health.",
        href: StaticRoutes.insuranceGroupIncomeProtection,
      },
      {
        key: "Group Dental Insurance",
        name: "Group Dental Insurance",
        iconName: "GroupDental",
        description:
          "Easy-access financial cover for oral treatment – for your people, and their families. Provided by Bupa.",
        href: StaticRoutes.insuranceGroupDentalInsurance,
        justifyContent: "space-around",
      },
    ],
    zigzag: {
      description: (
        <>
          Enrich your people’s lives with immediate access to{" "}
          <Anchor color={color.P100} href={StaticRoutes.featuresEmployeeAssistanceProgramme}>
            mental health
          </Anchor>{" "}
          and{" "}
          <Anchor color={color.P100} href={StaticRoutes.featuresVirtualGpServices}>
            virtual GP support
          </Anchor>
          , alongside mindfulness and fitness apps that drive daily engagement – and experience the
          power of a turn-key wellbeing programme.
        </>
      ),
      description2: (
        <>
          Make the world a better place – with global rewards that make a difference, and matter to
          your people. Plant trees, clean oceans, donate meals and measure your carbon footprint.
        </>
      ),
    },
    productSubheader: {
      subheader: (
        <>
          Level-up investment in your people with insurance that protects their futures, alongside
          wellbeing and global rewards that inspire a healthy life today. See how YuLife is
          attracting and retaining top talent with group insurance.
        </>
      ),
    },
  },
  us: {
    products: [
      {
        key: "For Individuals",
        name: "For Individuals",
        iconName: "ForIndividuals",
        description:
          "Boost your wellbeing with the world's most engaging wellness app and earn financial rewards for everyday health activities like walking!",
        href: StaticRoutes.ourSolutionsIndividuals,
      },
      {
        key: "For Businesses",
        name: "For Businesses",
        iconName: "ForBusinesses",
        description:
          "Bring your insurance benefits to life with protection that engages and improves the wellbeing of your people.",
        href: StaticRoutes.ourSolutionsBusinesses,
      },
      {
        key: "For Insurance Carriers",
        name: "For Insurance Carriers",
        iconName: "ForInsuranceCarriers",
        description:
          "Create a market-leading insurance and wellness ecosystem that drives growth and creates healthier populations.",
        href: StaticRoutes.ourSolutionsInsuranceCarriers,
      },
      {
        key: "For Brokers and Advisers",
        name: "For Brokers and Advisers",
        iconName: "ForBrokersAndAdvisors",
        description:
          "Offer your clients an  innovative insurance and wellbeing benefit with proven impact and meaningful value. ",
        href: StaticRoutes.ourSolutionsBrokersAndAdvisers,
      },
    ],
    zigzag: {
      description: (
        <>
          Enrich people’s lives with access to best-in-class mental and physical wellbeing
          prevention tools that drive daily engagement.
        </>
      ),
      description2: (
        <>
          Make the world a better place – with global rewards that make a difference, and matter to
          people. Plant trees, clean oceans, donate meals and measure your carbon footprint.
        </>
      ),
    },
    productSubheader: {
      subheader: (
        <>
          YuLife is a leading global insurtech and wellbeing company. We help prevent illness and
          protect lives by inspiring people to engage in their health and wellbeing every day. The
          end result is healthier individuals, thriving organizations and lower insurance risk.
        </>
      ),
    },
    caseStudyData: [
      {
        imgSrc: "/static/pages/home/yulife-us-capital-one-case-study.png",
        imgAlt:
          "Thumbnail image of the Capital One case study with YuLife. The image shows a white Capital One logo on employees at reception.",
        title: "How Capital One UK leveraged the YuLife app for the greater good",
        description:
          "Planting trees and raising £10,000 for charity: how Capital One UK leveraged the YuLife app for the greater good.",
        slug: `${StaticRoutes.caseStudies}capital-one-onboards-yulife/`,
        linkText: "Read case study",
      },
      {
        imgSrc: "/static/pages/home/yulife-us-what3words-case-study.png",
        imgAlt:
          "Thumbnail image of the What3Words case study with YuLife. The image shows a white What3Words logo on employees at sitting on a couch.",
        title: "Positioning wellbeing in the workplace at what3words",
        description:
          "This 150-person company is revolutionising location-sharing technology to protect and connect people across the world",
        slug: `${StaticRoutes.caseStudies}what-three-words-case-study/`,
        linkText: "Read case study",
      },
      {
        imgSrc: "/static/pages/home/yulife-us-rga-case-study.png",
        imgAlt:
          "Thumbnail image of the RGA case study with YuLife. The image shows a white RGA logo on a desk of employees working.",
        title: "Russell's Story: How YuLife Changed My Life For The Better",
        description:
          "YuLife superfan Russell Taplin discusses his life-changing experience with the app. He shares how interacting with YuLife daily enabled him to build some amazing long-term habits that have significantly improved his health.",
        slug: `${StaticRoutes.caseStudies}how-yulife-changed-my-life-for-the-better/`,
        linkText: "Read case study",
      },
    ],
  },
  za: {
    products: [
      {
        key: "Life Cover",
        name: "Life Cover",
        iconName: "",
        description:
          "Pays a lump sum to your employees’ nominated beneficiaries in the event of them or their spouse (if covered) passing.",
        href: "",
      },
      {
        key: "Income Protection",
        name: "Income Protection",
        iconName: "",
        description:
          "Covers a proportion of an employee’s income in the event that they are unable to work due to ill health.",
        href: "",
      },
      {
        key: "Funeral Cover",
        name: "Funeral Cover",
        iconName: "",
        description:
          "Pays a fixed amount to the employees family in the event of death. Cover can include their spouse and up to 5 children.",
        href: "",
      },
      {
        key: "Lump Sum Disability",
        name: "Lump Sum Disability",
        iconName: "",
        description:
          "Pays out a lump sum of money to the employee if they become permanently disabled.",
        href: "",
      },
      {
        key: "Education Benefit",
        name: "Education Benefit",
        iconName: "",
        description:
          "Provides money in the event of death or permanent disability (if selected) to help cover the costs of education and care needs.",
        href: "",
      },
    ],
    zigzag: {
      description: (
        <>
          Enrich your people’s lives with immediate access to{" "}
          <Anchor color={color.P100} href={StaticRoutes.featuresEmployeeAssistanceProgramme}>
            mental health
          </Anchor>{" "}
          and{" "}
          <Anchor color={color.P100} href={StaticRoutes.featuresVirtualGpServices}>
            virtual GP support
          </Anchor>
          , alongside mindfulness and fitness apps that drive daily engagement – and experience the
          power of a turn-key wellbeing programme.
        </>
      ),
      description2: (
        <>
          Make the world a better place – with global rewards that make a difference, and matter to
          your people. Plant trees, clean oceans, donate meals and measure your carbon footprint.
        </>
      ),
    },
    productSubheader: {
      subheader: (
        <>
          Level-up investment in your people with insurance that protects their futures, alongside
          wellbeing and global rewards that inspire a healthy life today. See how YuLife is
          attracting and retaining top talent with{" "}
          <Anchor color={color.P100} href={StaticRoutes.insuranceGroupInsurance}>
            group insurance
          </Anchor>
          .
        </>
      ),
    },
  },
  jp: {
    products: [],
    productSubheader: {
      subheader: null,
    },
  },
};

export default data;
